import type { CrmEventItemDto } from '@upme/contracts';

export type EventPeriod = 'all' | 'later' | `day:${number}`;
export type EventDateFilter = { key: EventPeriod; label: string };
function dayNumber(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const part = (type: string) => parts.find((p) => p.type === type)?.value;
  return Date.parse(`${part('year')}-${part('month')}-${part('day')}`) / 86400000;
}
export function buildEventDateFilters(
  events: ReadonlyArray<Pick<CrmEventItemDto, 'startsAt'>>,
  timeZone: string,
  now = new Date(),
): EventDateFilter[] {
  const today = dayNumber(now, timeZone);
  const days = [...new Set(events.map((event) => dayNumber(new Date(event.startsAt), timeZone)))].sort(
    (a, b) => a - b,
  );
  const filters: EventDateFilter[] = [{ key: 'all', label: 'Все ближайшие' }];
  let previousMonth: string | null = null;
  for (const day of days.filter((value) => value >= today && value < today + 7)) {
    const date = new Date(day * 86400000);
    const month = `${date.getUTCFullYear()}-${date.getUTCMonth()}`;
    const dateLabel = new Intl.DateTimeFormat('ru-RU', {
      timeZone: 'UTC',
      day: 'numeric',
      ...(month !== previousMonth ? { month: 'long' as const } : {}),
    }).format(date);
    const weekday = new Intl.DateTimeFormat('ru-RU', { timeZone: 'UTC', weekday: 'short' }).format(date);
    const prefix = day === today ? 'Сегодня · ' : day === today + 1 ? 'Завтра · ' : '';
    filters.push({ key: `day:${day}`, label: `${prefix}${dateLabel} · ${weekday}` });
    previousMonth = month;
  }
  if (days.some((day) => day >= today + 7)) filters.push({ key: 'later', label: 'Позже' });
  return filters;
}
export function matchesEventPeriod(
  event: Pick<CrmEventItemDto, 'startsAt'>,
  period: EventPeriod,
  timeZone: string,
  now = new Date(),
): boolean {
  if (period === 'all') return true;
  const day = dayNumber(new Date(event.startsAt), timeZone);
  if (period === 'later') return day >= dayNumber(now, timeZone) + 7;
  return day === Number(period.slice(4));
}
export function formatCrmEventDate(event: Pick<CrmEventItemDto, 'startsAt' | 'timeZone'>): string {
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: event.timeZone,
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(event.startsAt));
}
